import { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import Button from '../../components/ui/Button';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormStatus {
  type: 'success' | 'error' | null;
  message: string;
}

const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>({ type: null, message: '' });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear form status when user starts typing
    if (formStatus.type) {
      setFormStatus({ type: null, message: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setFormStatus({ type: null, message: '' });

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined,
          subject: formData.subject.trim(),
          message: formData.message.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        window.umami?.track('form-submit', {
          form: 'contact',
          source: window.location.pathname,
        });

        setFormStatus({
          type: 'success',
          message: result.message || t.contact.form.confirmation,
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      setFormStatus({
        type: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to send message. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <div className="bg-neutral-50 rounded-lg p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-red-100 p-2 rounded-full text-red-800">
            <MessageSquare size={20} />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-neutral-900">
            {t.contact.form.title}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form Status Message */}
          {formStatus.type && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg flex items-center gap-3 ${
                formStatus.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {formStatus.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p className="text-sm">{formStatus.message}</p>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-neutral-700 mb-2">
                {t.contact.form.name}*
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder={t.contact.form.placeholders.name}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-neutral-700 mb-2">
                {t.contact.form.email}*
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder={t.contact.form.placeholders.email}
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone" className="block text-neutral-700 mb-2">
              {t.contact.form.phone}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder={t.contact.form.placeholders.phone}
            />
          </div>

          <div>
            <label htmlFor="subject" className="block text-neutral-700 mb-2">
              {t.contact.form.subject}*
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder={t.contact.form.placeholders.subject}
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-neutral-700 mb-2">
              {t.contact.form.message}*
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={5}
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder={t.contact.form.placeholders.message}
            ></textarea>
          </div>

          <div>
            <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                t.contact.form.button
              )}
            </Button>
            {!formStatus.type && (
              <p className="text-sm text-neutral-500 mt-3 text-center">
                {t.contact.form.defaultNote}
              </p>
            )}
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default ContactForm;
